import { RecentlyViewed } from "@theme/recently-viewed-products";
import { sectionRenderer } from "@theme/section-renderer";

class RecentlyViewedSection extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: "0px 0px 400px 0px" },
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== "attributes") continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === "data-recommendations-performed" &&
        this.dataset.recommendationsPerformed === "true"
      )
        continue;

      // All other attribute changes trigger a reload
      // this.#loadRecentlyViewed();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecentlyViewed() {
    const { sectionId } = this.dataset;
    const id = this.id;

    const recentlyViewedItems = RecentlyViewed.getProductsHandles();

    if (!recentlyViewedItems || recentlyViewedItems.length === 0) {
      throw new Error("No Items Viewed");
      return;
    }

    this.#fetchRecentlyViewed(recentlyViewedItems)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement("div");
        console.log("result.data", result.data);
        html.innerHTML = result.data.join("") || "";
        if (this.querySelector(".resource-list")) {
          this.querySelector(".resource-list").innerHTML = html.innerHTML;
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string[] | undefined} recentlyViewedItems
   * @returns {Promise<{ success: true, data: (string | undefined)[] } | { success: false, status: number }>}
   */
  async #fetchRecentlyViewed(recentlyViewedItems) {
    console.log(recentlyViewedItems);

    if (!recentlyViewedItems) {
      return { success: false, status: 400 };
    }

    const itemsHTML = [];

    for (let index = 0; index < recentlyViewedItems.length; index++) {
      const handle = recentlyViewedItems[index];
      const x = await sectionRenderer.getSectionHTML(
        "template--28201506013532__section_cz6Ywx",
        false,
        new URL(`${Theme.routes.shop_url}/products/${handle}?view=card`),
      );

      const cardHTML = new DOMParser().parseFromString(x, "text/html");
      const card = cardHTML.querySelector("product-card");

      itemsHTML.push(card?.outerHTML);
    }

    return { success: true, data: itemsHTML };
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error("Product recommendations error:", error);
    this.classList.add("hidden");
    this.dataset.error = "Error loading product recommendations";
  }
}

if (!customElements.get("recently-viewed")) {
  customElements.define("recently-viewed", RecentlyViewedSection);
}
